import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import ImgCrop from "antd-img-crop";
import { Form, Input, DatePicker, Select, Upload } from "antd";
import LoadableButton from "../../../components/buttons/LoadableButton";
import { setUserDetails } from "../../../store/slices/userSlice";
import { GetUserApi, UpdateUserApi } from "../../../api/request/users";
import { GetAllCountryApi } from "../../../api/request/common";
import { CameraOutlined } from "@ant-design/icons";

export default function ProfileMobile() {
  const dispatch = useDispatch();
  const [updateForm] = Form.useForm();
  const { userDetails } = useSelector((state) => state.user);
  const [user, setUser] = useState([]);
  const [isLoadingProfileUpdate, setIsLoadingProfileUpdate] = useState(false);
  const [countries, setCountries] = useState([]);
  const [fileList, setFileList] = useState([]);

  const getUser = async () => {
    try {
      const { data } = await GetUserApi(userDetails?.id);
      setUser(data?.data);
      updateForm.setFieldsValue({
        FirstName: data?.data?.firstName,
        LastName: data?.data?.lastName,
        PhoneNo: data?.data?.phoneNo,
        ProfileImage: data?.data?.profileImagePath,
        DOB: data?.data?.dob ? dayjs(data?.data?.dob) : "",
        ExpAsIb: data?.data?.expAsIb,
        City: data?.data?.city,
        CountryOfBirth: data?.data?.countryOfBirthId,
      });
      dispatch(
        setUserDetails({
          ...userDetails,
          profileImage: data?.data?.profileImagePath,
        })
      );
    } catch (error) {}
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    updateForm.setFieldsValue({ profileImage: newFileList });
    if (newFileList.length === 0) {
      updateForm.setFieldsValue({ profileImage: null });
    }
  };

  const updateUserProfile = async (values) => {
    const extractOriginFileObj = (fileArray) =>
      fileArray && fileArray[0]?.originFileObj;
    try {
      setIsLoadingProfileUpdate(true);
      let apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };
      let params = {
        ...values,
        UserId: userDetails?.id,
        DOB: values["DOB"] ? dayjs(values["DOB"]).format("MM/DD/YYYY") : "",
      };
      if (values.profileImage && values.profileImage.length > 0) {
        params.ProfileImage = extractOriginFileObj(values.profileImage);
        delete params.profileImage;
      }
      const { data } = await UpdateUserApi({ ...params }, apiHeader);
      if (data?.success) {
        updateForm.resetFields();
        setFileList([]);
        toast.success(data?.message);
        getUser();
      }
      setIsLoadingProfileUpdate(false);
    } catch (error) {
      if (error?.response?.data?.error?.message) {
        toast.error(error?.response?.data?.error?.message);
      }
      setIsLoadingProfileUpdate(false);
    }
  };

  const getContriens = async () => {
    try {
      const { data } = await GetAllCountryApi();
      if (data?.success) {
        setCountries(
          data?.data?.map((item) => ({
            ...item,
            value: item?.id,
            label: item?.countryName,
          }))
        );
      }
    } catch (error) {}
  };

  const filterOption = (input, option) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  };

  const beforeUpload = (file) => {
    const isAllowedType =
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      file.type === "image/png";
    if (!isAllowedType) {
      toast.error("You can only upload JPG/JPEG/PNG files!");
      return Upload.LIST_IGNORE;
    }
    const isLt800K = file.size / 1024 < 800;
    if (!isLt800K) {
      toast.error("Image must be smaller than 800KB!");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  useEffect(() => {
    getUser();
    getContriens();
  }, []);

  return (
    <div className="mobile-dashboard min-h-screen">
      <Form
        form={updateForm}
        layout="vertical"
        autoComplete="off"
        onFinish={updateUserProfile}
      >
        <div className="flex justify-center mb-8">
          <div className="relative">
            <Form.Item
              name="profileImage"
              valuePropName="fileList"
              className="mb-0"
            >
              <ImgCrop rotationSlider showGrid>
                <Upload
                  fileList={fileList}
                  name="profileImage"
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  onChange={handleChange}
                >
                  <div className="relative cursor-pointer">
                    {fileList.length > 0 ? (
                      <img
                        src={URL.createObjectURL(fileList[0].originFileObj)}
                        alt="avatar"
                        className="w-[108px] h-[108px] rounded-full object-cover border-4 border-blue-200"
                      />
                    ) : user?.profileImagePath ? (
                      <img
                        src={user.profileImagePath}
                        alt="avatar"
                        className="w-[108px] h-[108px] rounded-full object-cover border-4 border-blue-200"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center border-4 border-blue-200">
                        <span className="text-white text-2xl font-semibold uppercase">
                          {user?.firstName?.charAt(0) || "U"}
                          {user?.lastName?.charAt(0) || "S"}
                        </span>
                      </div>
                    )}

                    {/* Camera Icon Overlay */}
                    <div className="absolute bottom-0 right-0 w-10 h-10 bg-primary-mobile rounded-full flex items-center justify-center">
                      <CameraOutlined className="text-white text-xl" />
                    </div>
                  </div>
                </Upload>
              </ImgCrop>
            </Form.Item>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded-2xl">
          <div className="flex gap-2">
            <div className="w-full">
              <label className="text-sm text-[#2F2B3DCC] font-medium">
                First Name
              </label>
              <Form.Item
                name="FirstName"
                rules={[
                  { required: true, message: "Please enter first name." },
                ]}
              >
                <Input
                  placeholder="First name"
                  className="mt-1 w-full py-3 rounded-[14px] border-[#E2E8F0]"
                />
              </Form.Item>
            </div>

            <div className="w-full">
              <label className="text-sm text-[#2F2B3DCC] font-medium">
                Last Name
              </label>
              <Form.Item
                name="LastName"
                rules={[{ required: true, message: "Please enter last name." }]}
              >
                <Input
                  placeholder="Last name"
                  className="mt-1 w-full py-3 rounded-[14px] border-[#E2E8F0]"
                />
              </Form.Item>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="w-full">
              <label className="text-sm text-[#2F2B3DCC] font-medium">
                Phone No.
              </label>
              <Form.Item
                name="PhoneNo"
                rules={[
                  {
                    required: true,
                    message: "Please enter valid phone number.",
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder="Phone no."
                  className="mt-1 w-full py-3 rounded-[14px] border-[#E2E8F0]"
                />
              </Form.Item>
            </div>

            <div className="input-white w-full">
              <label className="text-sm text-[#2F2B3DCC] font-medium">
                Date Of Birth
              </label>
              <Form.Item name="DOB">
                <DatePicker
                  placeholder="Date of birth"
                  className="mt-1 w-full py-3 rounded-[14px] border-[#E2E8F0]"
                  format="MM/DD/YYYY"
                />
              </Form.Item>
            </div>
          </div>

          <div>
            <label className="text-sm text-[#2F2B3DCC] font-medium">City</label>
            <Form.Item name="City">
              <Input
                placeholder="City"
                className="mt-1 w-full py-3 rounded-[14px] border-[#E2E8F0]"
              />
            </Form.Item>
          </div>

          <div className="flex gap-2">
            <div className="w-full">
              <label className="text-sm text-[#2F2B3DCC] font-medium">
                Experience As IB
              </label>
              <Form.Item name="ExpAsIb">
                <Input
                  type="number"
                  placeholder="Experience as ib"
                  className="mt-1 w-full py-3 rounded-[14px] border-[#E2E8F0]"
                />
              </Form.Item>
            </div>

            <div className="w-full">
              <label className="text-sm text-[#2F2B3DCC] font-medium">
                Country
              </label>
              <Form.Item name="CountryOfBirth">
                <Select
                  showSearch
                  placeholder="Select a country"
                  className="w-full mt-1 ant-select-selector-white country-profile-select"
                  filterOption={filterOption}
                  options={countries}
                  popupClassName="custom-select-dropdown"
                  dropdownAlign={{ offset: [0, 0] }}
                />
              </Form.Item>
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-4">
          <LoadableButton
            type="submit"
            className="bg-primary-mobile w-full text-sm text-white font-medium px-6 py-4 rounded-lg themeHover duration-500"
            lable="Save changes"
            loadingLable="Saving..."
            isLoading={isLoadingProfileUpdate}
            disabled={isLoadingProfileUpdate}
          />
        </div>
      </Form>
    </div>
  );
}
